import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import NavigationBar from './components/common/NavigationBar';
import TeacherView from './views/TeacherView';
import UTPView from './views/UTPView';
import AdminView from './views/AdminView';
import MasterView from './views/MasterView';
import LoginView from './views/LoginView';
import ForcePasswordChangeView from './views/ForcePasswordChangeView';
import { NotificationProvider } from './context/NotificationContext';
import { CurriculumProvider } from './context/CurriculumContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { FeatureFlagProvider } from './context/FeatureFlagContext';
import { FeedbackProvider } from './context/FeedbackContext';
import GlobalNotificationModal from './components/common/GlobalNotificationModal';

/**
 * Ruta protegida por autenticación Y tipo de usuario.
 * - Si no está autenticado → redirige a /login.
 * - Si está autenticado pero su user_type no coincide → redirige a su ruta home.
 */
function ProtectedRoute({ requiredType, children }) {
  const { isAuthenticated, user, homeRoute } = useAuth();

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user.user_type !== requiredType) return <Navigate to={homeRoute} replace />;

  return children;
}

/** Layout con navbar + contenido principal */
function WithNavbar({ children }) {
  return (
    <>
      <NavigationBar />
      {children}
    </>
  );
}

function AppRoutes() {
  const { isAuthenticated, user, homeRoute } = useAuth();

  // Contraseña generada por un admin: se bloquea la app hasta personalizarla.
  if (isAuthenticated && user?.must_change_password) {
    return <ForcePasswordChangeView />;
  }

  return (
    <Routes>
      {/* Ruta pública */}
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to={homeRoute} replace /> : <LoginView />}
      />

      {/* Vista Docente */}
      <Route
        path="/docente/*"
        element={
          <ProtectedRoute requiredType="teacher">
            <WithNavbar><TeacherView /></WithNavbar>
          </ProtectedRoute>
        }
      />

      {/* Vista Jefe de UTP */}
      <Route
        path="/utp/*"
        element={
          <ProtectedRoute requiredType="utp">
            <WithNavbar><UTPView /></WithNavbar>
          </ProtectedRoute>
        }
      />

      {/* Vista Administrador */}
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute requiredType="admin">
            <WithNavbar><AdminView /></WithNavbar>
          </ProtectedRoute>
        }
      />

      {/* Vista Maestra */}
      <Route
        path="/master/*"
        element={
          <ProtectedRoute requiredType="master">
            <WithNavbar><MasterView /></WithNavbar>
          </ProtectedRoute>
        }
      />

      {/* Raíz */}
      <Route
        path="/"
        element={<Navigate to={isAuthenticated ? homeRoute : '/login'} replace />}
      />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <FeatureFlagProvider>
        <CurriculumProvider>
          <NotificationProvider>
            <FeedbackProvider>
              <Router>
                <div className="artemis-layout">
                  <AppRoutes />
                  <GlobalNotificationModal />
                </div>
              </Router>
            </FeedbackProvider>
          </NotificationProvider>
        </CurriculumProvider>
      </FeatureFlagProvider>
    </AuthProvider>
  );
}

export default App;
